<template>
  <div :class="[classes]">
    <div class="relative">
      <Listbox as="div" class="mr-5 w-full" :horizontal="isHorizontal">
        <div class="w-full flex justify-between items-center">
          <ListboxLabel v-if="showLabel" class="w-full text-base font-medium text-common-grey">
            {{ label }}
          </ListboxLabel>
          <div v-if="showSearch" class="w-full text-common-grey flex flex-row-reverse items-center gap-4">
            <div class="relative w-full h-[50px] flex items-center flex-row-reverse">
              <div class="right-2 absolute w-5 h-full flex justify-center items-center">
                <transition name="fade" mode="out-in" duration="100">
                  <X
                    v-if="is_search_open"
                    class="w-5 h-5 text-gray-400 cursor-pointer"
                    @click="
                      () => {
                        is_search_open = false
                        $refs['tab-search-input'].blur()
                      }
                    " />
                  <Search
                    v-else
                    class="h-5 w-5 text-gray-400 cursor-pointer"
                    @click="
                      () => {
                        is_search_open = true
                        $refs['tab-search-input'].focus()
                      }
                    " />
                </transition>
              </div>

              <input
                ref="tab-search-input"
                v-model="search_text"
                label=""
                :class="[is_search_open ? 'opacity-100 w-full' : 'opacity-0 w-0 -z-10']"
                class="transition-all ease-in appearance-none block w-full px-3 py-2 border dark:bg-dark-box-background dark:text-white dark:border-dark-divider-border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
                name="name"
                autocomplete="off"
                type="text"
                :placeholder="$t('Search...')">
            </div>
          </div>
        </div>
        <div class="relative w-full">
          <draggable
            v-model="items_clone"
            as="ListboxOptions"
            static
            :unmount="false"
            :class="[
              !isBoxBackground
                ? 'bg-light-screen-background dark:bg-dark-screen-background'
                : 'bg-light-box-background dark:border-dark-divider-border border-light-divider-border dark:bg-dark-box-background',
              errorMessage
                ? 'border-status-error-red text-status-error-red border-[1px] placeholder-status-error-red focus:outline-none focus:ring-status-error-red focus:border-status-error-red '
                : '',
              isHorizontal ? 'flex flex-row gap-2 bg-white p-1 min-h-[45px]' : 'resize-y min-h-[45px] h-full',

              ` mt-1 w-full border-[1px] ${
                $store.state.ui.darkMode ? 'dark-scrollbar' : ''
              } dark:border-dark-divider-border rounded-lg text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm transition ease-in-out duration-side-menu-transition-duration`
            ]"
            filter=".ignore-elements"
            :component-data="{ type: 'transition-group', name: !drag ? 'flip-list' : null }"
            v-bind="{
              animation: 200,
              group: 'items',
              disabled: !isDraggable,
              ghostClass: 'ghost',
              chosenClass: 'chosen'
            }"
            :item-key="valueAttr"
            @start="drag = true"
            @end="
              () => {
                drag = false
                handleItemsChange()
              }
            ">
            <template #item="{ element }">
              <ListboxOption
                v-if="searchResultItems.filter((i) => i.id == element.id).length"
                :key="element.id"
                as="template"
                :value="getValue(element)"
                @click="handleSelect(element)">
                <div
                  :class="[
                    isHorizontal ? 'bg-gray-200 pr-8' : '',
                    requiredItems?.includes(element.reference_name) ? '!bg-gray-200 hover:text-black dark:hover:bg-dark-brand-blue' : ''
                  ]"
                  class="flex group hover:bg-light-brand-blue dark:hover:bg-dark-brand-blue cursor-default rounded-lg relative py-2 pl-1 pr-4 transition ease-in-out duration-side-menu-transition-duration">
                  <span :class="{ 'opacity-100': element.selected }" class="px-1 opacity-0 inset-y-0 left-0 flex items-center pl-1.5">
                    <Check
                      :class="[
                        requiredItems?.includes(element.reference_name) ? '' : 'group-hover:text-white',
                        'text-light-brand-blue dark:text-dark-brand-blue h-5 w-5 transition ease-in-out duration-side-menu-transition-duration'
                      ]"
                      aria-hidden="true" />
                  </span>
                  <span
                    :class="[
                      element.selected
                        ? 'text-light-body-text dark:text-dark-body-text group-hover:text-dark-body-text dark:group-hover:text-light-body-text'
                        : 'text-common-grey group-hover:text-dark-body-text',
                      requiredItems?.includes(element.reference_name)
                        ? 'group-hover:text-light-body-text group-hover:dark:text-dark-body-text'
                        : '',
                      'block truncate transition ease-in duration-side-menu-transition-duration'
                    ]">
                    {{ getLabel(element) }}
                  </span>
                </div>
              </ListboxOption>
            </template>
          </draggable>
        </div>
      </Listbox>
      <p
        class="first-letter:capitalize absolute right-0 leading-tight pt-[1px] text-[12px] text-red-400"
        :class="[errorMessage || meta.valid ? 'visible' : 'invisible']">
        {{ errorMessage || successMessage }}
      </p>
      <div v-if="min_limit_msg" class="text-red-error absolute">
        {{ $t("You have to select one item it least") }}
      </div>
      <div v-if="max_limit" class="text-red-error absolute">
        {{ $t("You can only select a maximum of") }} {{ limit }} {{ $t("values") }}
      </div>
    </div>
  </div>
</template>

<script>
import draggable from "vuedraggable"
import { useField } from "vee-validate"
import { Search, X, Check } from "lucide-vue-next"
import { Listbox, ListboxLabel, ListboxOption } from "@headlessui/vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
export default {
  components: {
    Search,
    X,
    draggable,
    Listbox,
    ListboxLabel,
    ListboxOption,
    Check
  },
  props: {
    actionName: {
      type: String,
      default: null,
      required: false
    },
    limit: {
      type: Number,
      required: false
    },
    selectionRequired: {
      type: Boolean,
      default: true
    },

    items: {
      type: Array,
      required: true
    },
    requiredItems: {
      type: Array,
      required: false
    },
    showLabel: {
      type: Boolean,
      default: false
    },
    classes: {
      type: String,
      default: ""
    },
    value: {
      type: Array,
      required: true
    },
    name: {
      type: String,
      required: true
    },

    label: {
      type: String,
      required: true
    },
    labelAttr: {
      type: String,
      validator(v) {
        return v !== undefined
      }
    },
    valueAttr: {
      validator(v) {
        return v !== undefined
      }
    },
    isDraggable: {
      type: Boolean,
      required: false
    },
    successMessage: {
      type: String,
      default: ""
    },
    isHorizontal: {
      type: Boolean,
      default: false
    },
    showSearch: {
      type: Boolean,
      default: true
    },
    isSearchExpanded: {
      type: Boolean,
      default: true
    },
    isBoxBackground: {
      type: Boolean,
      default: false
    },
    returnObjects: {
      type: Boolean,
      default: false
    }
  },
  emits: ["update:currentItems", "search"],
  setup(props) {
    const { errorMessage, meta } = useField(props.name, undefined, {
      initialValue: props.value ? props.value : []
    })
    return {
      errorMessage,
      meta
    }
  },
  data() {
    return {
      search_text: "",
      items_clone: [],
      is_search_open: false,
      drag: false,
      max_limit: false,
      min_limit_msg: false
    }
  },
  computed: {
    searchResultItems() {
      return this.items_clone.filter((i) => this.getLabel(i).toLowerCase().includes(this.search_text.toLowerCase()))
    }
  },

  watch: {
    items() {
      this.initItemsClone()
    },
    search_text() {
      this.$emit("search", this.search_text)
    }
  },
  created() {
    this.is_search_open = this.isSearchExpanded
    this.initItemsClone()
  },
  methods: {
    initItemsClone() {
      //create an array displaying the selected items first (in order) then the remaining items
      const remaining_items = this.items.filter((item) => !this.value.find((sitem) => item?.id === sitem?.id))
      //add a selected=true property for all selected items
      const checked_items = this.value
        .filter((x) => x)
        .map((v) => {
          return { ...v, selected: true }
        })
      this.items_clone = this.$utils.clone(checked_items.concat(remaining_items))
      if (this.actionName) {
        this.items_clone.push({ name: `+ ${this.actionName}`, type: "action" })
      }
    },
    startAction() {
      if (this.actionName === this.$t("Create new goal")) {
        this.$emitter.emit(ALL_EMITTERS.CREATE_GOAL)
      } else if (this.actionName === this.$t("Create new task")) {
        this.$emitter.emit(ALL_EMITTERS.CREATE_TASK)
      }
    },

    handleItemsChange() {
      const selected_items = []
      const unselected_item = []

      this.items_clone.forEach((i) => {
        if (i.selected) {
          selected_items.push(i)
        } else {
          unselected_item.push(i)
        }
      })
      this.items_clone = this.$utils.clone(selected_items.concat(unselected_item))
      this.updateState()
    },
    handleSelect(item) {
      if (!this.requiredItems?.includes(item.reference_name)) {
        if (item.type === "action") {
          this.startAction()
        } else {
          if (item.selected && this.items_clone.filter((item) => item.selected).length == 1 && this.selectionRequired) {
            this.min_limit_msg = true
            setTimeout(() => {
              this.min_limit_msg = false
            }, 1500)
          } else if (!item.selected && this.limit && this.items_clone.filter((item) => item.selected).length == this.limit) {
            this.max_limit = true
            setTimeout(() => {
              this.max_limit = false
            }, 1500)
          } else {
            item.selected = !item.selected
            this.handleItemsChange()
          }
        }
      }
    },

    updateState() {
      //update "currentItems" array with new order of selected items, "currentItems" should be always linked with a list in the parent component
      if (this.returnObjects) {
        this.$emit(
          "update:currentItems",
          this.items_clone.filter((item) => item.selected)
        )
      } else {
        this.$emit(
          "update:currentItems",
          this.items_clone.filter((item) => item.selected).map((item) => this.getValue(item))
        )
      }
    },

    getLabel(item) {
      if (typeof item == "object" && this.labelAttr) {
        return item[this.labelAttr]
      }
      return item
    },
    getValue(item) {
      if (typeof item == "object" && this.valueAttr) {
        return item[this.valueAttr]
      }
      return item
    }
  }
}
</script>

<style>
.ghost {
  @apply opacity-50 bg-blue border-0;
}
.chosen {
  @apply opacity-50 bg-blue border-0;
}
.flip-list {
  transition: all 0.25s ease-out;
}
</style>
